import { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { DATEPICKER_FORMAT } from 'constants/dates'
import { format, isValid, parse } from 'date-fns'
import type { SearchOption } from '@jana-technology/platform-ui'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'

import { SelectDropdownWrapper } from 'common/components/SelectDropdownWrapper'

import { selectStringAttributeDetails } from 'features/RiskManagement/Assessments/Scenarios/whatIfScenarioSelectors'

import { attributeTypes } from './attributeTypes'
import { stringRuleBool } from './stringRuleBool'
import type { IConditionWithId, IStringAttributeOptions } from './types'

// #region typescript stuff
interface IAttributeRule {
  attributeType: string
  attributeName: string
  attributeCode: string
  selectedCondition: IConditionWithId
  setConditions: React.Dispatch<React.SetStateAction<IConditionWithId[]>>
  disableDropdownClear?: boolean
}

/*
TODO Refactore how we get RuleActions
 - Frontend should use enums/RuleActions that it gets from backend instead of using local enums when
   the attribute type is not STRING.
 - Database table event.EventTableAttributeEnum should be populated with enums for all attributes,
   for now, it's only populated with enums for attributes that are of type STRING.
*/
enum numberRuleConditions {
  LESS_THAN = 'Less Than',
  BETWEEN = 'Between',
  GREATER_THAN = 'Greater Than'
}

enum nullableNumberRuleConditions {
  LESS_THAN = 'Less Than',
  BETWEEN = 'Between',
  GREATER_THAN = 'Greater Than',
  NULL = 'Null'
}

enum dateRuleConditions {
  LESS_THAN = 'Less Than',
  BETWEEN = 'Between',
  GREATER_THAN = 'Greater Than',
  NULL = 'Null'
}

enum numberRuleActions {
  REPLACE_WITH = 'Replace With',
  ABSOLUTE_INCREASE = 'Absolute Increase',
  ABSOLUTE_DECREASE = 'Absolute Decrease',
  RELATIVE_INCREASE = 'Relative Increase',
  RELATIVE_DECREASE = 'Relative Decrease'
}

enum yearRuleActions {
  REPLACE_WITH = 'Replace With'
}

type BeforeData = {
  option: string
  beforeValue: string
  setBeforeValue: React.Dispatch<React.SetStateAction<string>>
}

type AfterData = {
  option: string
  afterValue: string
  setAfterValue: React.Dispatch<React.SetStateAction<string>>
}

type BaseInputCompProps = {
  updateConditions: (conditionElement: Partial<IConditionWithId>) => void
}

type TypeConditionsProps = BaseInputCompProps & BeforeData
type TypeActionsProps = BaseInputCompProps & AfterData
// #endregion

const numberInputClassName = 'w-24 rounded font-semibold'

const attributesWithNullNumberCondition = new Set([
  'ExternalCorrosionRate',
  'InternalCorrosionRate',
  'ReassessmentInterval'
])

// empty string is not a valid input for date-fns functions; nullify it
// return a pair to destructure and avoid MUI uncontrolled/controlled errors
const processDateRangeString = (dateRange: string) => {
  const _dates = dateRange
    .split(' - ')
    .map((dateString) => dateString.trim() || null)

  return _dates
}

const InputNumberTypeCondition = ({
  option,
  beforeValue,
  setBeforeValue,
  updateConditions
}: TypeConditionsProps) => {
  if (option === nullableNumberRuleConditions.NULL) {
    return null
  }

  if (option === numberRuleConditions.BETWEEN) {
    return (
      <>
        <input
          type='number'
          className={numberInputClassName}
          value={beforeValue.split('-')[0]}
          onChange={(e) => {
            const adjustedValue = `${e.target.value}-${
              beforeValue.split('-')[1]
            }`

            setBeforeValue(adjustedValue)

            updateConditions({ beforeValue: adjustedValue })
          }}
        />
        <label className='mx-1 font-bold'>–</label>
        <input
          type='number'
          className={numberInputClassName}
          value={beforeValue.split('-')[1]}
          onChange={(e) => {
            const adjustedValue = `${beforeValue.split('-')[0]}-${
              e.target.value
            }`

            setBeforeValue(adjustedValue)

            updateConditions({ beforeValue: adjustedValue })
          }}
        />
      </>
    )
  } else {
    return (
      <input
        type='number'
        className={numberInputClassName}
        value={beforeValue}
        onChange={(e) => {
          const { value } = e.target

          setBeforeValue(value)

          updateConditions({ beforeValue: value })
        }}
      />
    )
  }
}

const InputDateTypeCondition = ({
  option,
  beforeValue,
  setBeforeValue,
  updateConditions
}: TypeConditionsProps) => {
  const [fromDate, toDate] = processDateRangeString(beforeValue)
  const _referenceDate = new Date()

  const handleBeforeValue = (beforeValue: string) => {
    setBeforeValue(beforeValue)

    updateConditions({ beforeValue })
  }

  if (option === numberRuleConditions.BETWEEN) {
    return (
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <DatePicker
          className='w-fit'
          format={DATEPICKER_FORMAT}
          value={fromDate && parse(fromDate, DATEPICKER_FORMAT, _referenceDate)}
          onChange={(date) => {
            if (isValid(date)) {
              handleBeforeValue(
                `${format(date!, DATEPICKER_FORMAT)} - ${toDate || ''}`
              )
            } else {
              handleBeforeValue(` - ${toDate || ''}`)
            }
          }}
        />
        <span className='mx-1 font-bold'>–</span>
        <DatePicker
          className='w-fit'
          format={DATEPICKER_FORMAT}
          value={toDate && parse(toDate, DATEPICKER_FORMAT, _referenceDate)}
          onChange={(date) => {
            if (isValid(date)) {
              handleBeforeValue(
                `${fromDate || ''} - ${format(date!, DATEPICKER_FORMAT)}`
              )
            } else {
              handleBeforeValue(`${fromDate || ''} - `)
            }
          }}
        />
      </LocalizationProvider>
    )
  } else if (option === dateRuleConditions.NULL) {
    return null
  } else {
    return (
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <DatePicker
          className='w-fit'
          format={DATEPICKER_FORMAT}
          value={fromDate && parse(fromDate, DATEPICKER_FORMAT, _referenceDate)}
          onChange={(date) => {
            if (isValid(date)) {
              handleBeforeValue(format(date!, DATEPICKER_FORMAT))
            } else {
              handleBeforeValue('')
            }
          }}
        />
      </LocalizationProvider>
    )
  }
}

const InputDateTypeAction = ({
  afterValue,
  setAfterValue,
  updateConditions
}: TypeActionsProps) => {
  const [fromDate] = processDateRangeString(afterValue)
  const _referenceDate = new Date()

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <DatePicker
        className='w-auto'
        format={DATEPICKER_FORMAT}
        value={fromDate && parse(fromDate, DATEPICKER_FORMAT, _referenceDate)}
        onChange={(date) => {
          if (isValid(date)) {
            const formattedValue = format(date!, DATEPICKER_FORMAT)
            setAfterValue(formattedValue)
            updateConditions({ afterValue: formattedValue })
          } else {
            setAfterValue('')
            updateConditions({ afterValue: '' })
          }
        }}
      />
    </LocalizationProvider>
  )
}

const InputNumberTypeAction = ({
  option,
  afterValue,
  setAfterValue,
  updateConditions
}: TypeActionsProps) => {
  if (
    option === numberRuleActions.RELATIVE_INCREASE ||
    option === numberRuleActions.RELATIVE_DECREASE
  ) {
    return (
      <>
        <input
          type='number'
          className={numberInputClassName}
          value={afterValue}
          onChange={(e) => {
            const { value } = e.target

            setAfterValue(value)

            updateConditions({ afterValue: value })
          }}
        />
        <label className='mx-1 font-bold'>%</label>
      </>
    )
  } else {
    return (
      <input
        type='number'
        className={numberInputClassName}
        value={afterValue}
        onChange={(e) => {
          const { value } = e.target

          setAfterValue(value)

          updateConditions({ afterValue: value })
        }}
      />
    )
  }
}

const AttributeRule = ({
  attributeType,
  attributeName,
  attributeCode,
  selectedCondition,
  setConditions,
  disableDropdownClear = false
}: IAttributeRule) => {
  const [beforeCondition, setBeforeCondition] = useState<string>(
    selectedCondition === undefined ? '' : selectedCondition.beforeCondition
  )

  const [afterCondition, setAfterCondition] = useState<string>(
    selectedCondition === undefined ? '' : selectedCondition.afterCondition
  )

  const [beforeValue, setBeforeValue] = useState<string>(
    selectedCondition === undefined ? '' : String(selectedCondition.beforeValue)
  )

  const [afterValue, setAfterValue] = useState<string>(
    selectedCondition === undefined ? '' : String(selectedCondition.afterValue)
  )

  const stringOptions = useSelector(selectStringAttributeDetails)

  const dropDownClass = 'mx-2 w-48 text-black font-semibold'

  const updateConditions = useCallback(
    (conditionElement: Partial<IConditionWithId>) => {
      setConditions((state) =>
        state.map((condition) => {
          if (condition.id === selectedCondition.id) {
            return {
              ...condition,
              ...conditionElement
            }
          }

          return condition
        })
      )
    },
    [selectedCondition.id, setConditions]
  )

  const getOptionsForStringAttribute = useCallback(
    (attributeCode: string): IStringAttributeOptions[] | undefined => {
      for (let i = 0; i < stringOptions.length; i++) {
        if (stringOptions[i].code === attributeCode) {
          return stringOptions[i].options
        }
      }
    },
    [stringOptions]
  )

  const firstFourCharacters = (year: number) => {
    return year.toString().slice(0, 4)
  }

  useEffect(() => {
    setBeforeCondition(selectedCondition?.beforeCondition)
    setAfterCondition(selectedCondition?.afterCondition)

    setBeforeValue(
      selectedCondition?.beforeValue === undefined
        ? ''
        : attributeName.toLowerCase().includes('year')
          ? firstFourCharacters(Number(selectedCondition?.beforeValue))
          : String(selectedCondition.beforeValue)
    )

    setAfterValue(
      selectedCondition?.beforeValue === undefined
        ? ''
        : attributeName.toLowerCase().includes('year')
          ? firstFourCharacters(Number(selectedCondition?.afterValue))
          : String(selectedCondition.afterValue)
    )
  }, [attributeName, selectedCondition])

  // ! this effect should be scrapped also
  useEffect(() => {
    if (attributeType === attributeTypes.STRING) {
      const defaultValue1 =
        getOptionsForStringAttribute(attributeCode)?.[0].code

      const defaultValue2 =
        getOptionsForStringAttribute(attributeCode)?.[1]?.code

      if (defaultValue1 != null && defaultValue2 != null) {
        if (beforeValue === '' && afterValue === '') {
          setBeforeValue(defaultValue1)
          setAfterValue(defaultValue2)

          updateConditions({
            beforeValue: defaultValue1,
            afterValue: defaultValue2
          })
        }

        if (beforeValue === afterValue) {
          const finalValue =
            beforeValue === defaultValue1 ? defaultValue2 : defaultValue1

          setAfterValue(finalValue)

          updateConditions({
            afterValue: finalValue
          })
        }
      }
    }
  }, [
    afterValue,
    attributeCode,
    attributeType,
    beforeValue,
    getOptionsForStringAttribute,
    updateConditions
  ])

  if (attributeType === attributeTypes.BOOLEAN) {
    const booleanAttributeOptions: SearchOption[] = Object.values(
      stringRuleBool
    ).map((option) => {
      return {
        label: option,
        value: option
      }
    })

    return (
      <div className='flex h-12 w-full gap-1 text-gray-700'>
        <div className='flex items-center rounded bg-white px-3'>
          <span className='font-semibold'>IF</span>
        </div>
        <div className='flex w-full items-center rounded bg-white px-3'>
          <span>{attributeName} Is</span>
          <SelectDropdownWrapper
            className={dropDownClass}
            disableClear={disableDropdownClear}
            options={booleanAttributeOptions}
            value={beforeValue}
            onChange={(_, selection) => {
              if (
                selection == null ||
                Array.isArray(selection) ||
                !selection.value
              ) {
                return
              }

              const { value } = selection

              const afterVal =
                value === stringRuleBool.TRUE
                  ? stringRuleBool.FALSE
                  : stringRuleBool.TRUE

              setBeforeValue(value as stringRuleBool)
              setAfterValue(afterVal)

              updateConditions({
                beforeValue: value as stringRuleBool,
                afterValue: afterVal
              })
            }}
          />
          <span>Replace With</span>
          <SelectDropdownWrapper
            className={dropDownClass}
            disableClear={disableDropdownClear}
            options={booleanAttributeOptions.filter(
              (options) => options.value !== beforeValue
            )}
            value={afterValue}
            onChange={(_, selection) => {
              if (Array.isArray(selection)) {
                return
              }

              const { value } = selection

              setAfterValue(value as stringRuleBool)

              updateConditions({
                afterValue: value as stringRuleBool
              })
            }}
          />
        </div>
      </div>
    )
  } else if (
    attributeType === attributeTypes.INTEGER ||
    attributeType === attributeTypes.FLOAT
  ) {
    const numberConditionOptions = Object.values(
      attributesWithNullNumberCondition.has(attributeCode)
        ? nullableNumberRuleConditions
        : numberRuleConditions
    ).map((condition) => {
      return {
        label:
          condition.toLocaleLowerCase() === 'null'
            ? condition.toLocaleUpperCase()
            : condition,
        value: condition
      } satisfies SearchOption
    })

    return (
      <div className='flex h-12 w-full gap-1 text-gray-700'>
        <div className='flex items-center rounded bg-white px-3'>
          <span className='font-semibold'>IF</span>
        </div>
        <div className='flex w-full items-center rounded bg-white px-3'>
          <span>{attributeName} Is</span>
          <SelectDropdownWrapper
            className={dropDownClass}
            disableClear={disableDropdownClear}
            options={numberConditionOptions}
            value={beforeCondition}
            onChange={(_, selection) => {
              if (Array.isArray(selection)) {
                return
              }

              const { value } = selection

              setBeforeCondition(value as string)

              if (value === numberRuleConditions.BETWEEN) {
                setBeforeValue(' - ')

                updateConditions({
                  beforeCondition: value,
                  beforeValue: ' - '
                })
              } else if (value === nullableNumberRuleConditions.NULL) {
                setBeforeValue('')

                updateConditions({
                  beforeCondition: value as string,
                  beforeValue: 'NULL'
                })
              } else {
                setBeforeValue('')

                updateConditions({
                  beforeCondition: value as string,
                  beforeValue: ''
                })
              }
            }}
          />
          <InputNumberTypeCondition
            option={beforeCondition}
            beforeValue={beforeValue}
            setBeforeValue={setBeforeValue}
            updateConditions={updateConditions}
          />
          <SelectDropdownWrapper
            className={dropDownClass}
            disableClear={disableDropdownClear}
            options={Object.values(
              attributeName.toLowerCase().includes('year')
                ? yearRuleActions
                : numberRuleActions
            ).map((condition) => {
              return {
                label: condition,
                value: condition
              } satisfies SearchOption
            })}
            value={afterCondition}
            onChange={(_, selection) => {
              if (Array.isArray(selection)) {
                return
              }

              const { value } = selection

              setAfterCondition(value as string)
              setAfterValue('')

              updateConditions({
                afterCondition: value as string,
                afterValue: ''
              })
            }}
          />
          <InputNumberTypeAction
            option={afterCondition}
            afterValue={afterValue}
            setAfterValue={setAfterValue}
            updateConditions={updateConditions}
          />
        </div>
      </div>
    )
  } else if (attributeType === attributeTypes.STRING) {
    const stringAttributeOptions = Object.values(
      getOptionsForStringAttribute(attributeCode) ?? []
    ).map((option: IStringAttributeOptions) => {
      return {
        label: option.name,
        value: option.code
      } satisfies SearchOption
    })

    return (
      <div className='flex h-12 w-full gap-1 text-gray-700'>
        <div className='flex items-center rounded bg-white px-3'>
          <span className='font-semibold'>IF</span>
        </div>
        <div className='flex w-full items-center rounded bg-white px-3'>
          <span>{attributeName} Is</span>
          <SelectDropdownWrapper
            className={dropDownClass}
            disableClear={disableDropdownClear}
            options={stringAttributeOptions}
            value={beforeValue}
            onChange={(_, selection) => {
              if (Array.isArray(selection)) {
                return
              }

              const { value } = selection

              setBeforeValue(value as string)
              updateConditions({ beforeValue: value as string })
            }}
          />
          <span>Replace With</span>
          <SelectDropdownWrapper
            className={dropDownClass}
            disableClear={disableDropdownClear}
            options={stringAttributeOptions}
            value={afterValue}
            onChange={(_, selection) => {
              if (Array.isArray(selection)) {
                return
              }

              const { value } = selection

              setAfterValue(value as string)
              updateConditions({ afterValue: value as string })
            }}
          />
        </div>
      </div>
    )
  } else if (attributeType === attributeTypes.DATE) {
    const dateConditionOptions: SearchOption[] = Object.values(
      dateRuleConditions
    ).map((condition) => ({
      label:
        condition.toLocaleLowerCase() === 'null'
          ? condition.toLocaleUpperCase()
          : condition,
      value: condition
    }))

    return (
      <div className='flex h-12 w-full gap-1 text-gray-700'>
        <div className='flex items-center rounded bg-white px-3'>
          <span className='font-semibold'>IF</span>
        </div>
        <div className='flex w-full items-center rounded bg-white px-3'>
          <span data-testid='attribute-name'>{attributeName} Is</span>
          <SelectDropdownWrapper
            className={dropDownClass}
            data-testid='rule-before-condition'
            disableClear={disableDropdownClear}
            options={dateConditionOptions}
            value={beforeCondition}
            onChange={(_, selection) => {
              if (Array.isArray(selection)) {
                return
              }

              const { value } = selection

              setBeforeCondition(value as string)

              if (value === dateRuleConditions.BETWEEN) {
                setBeforeValue(' - ')

                updateConditions({
                  beforeCondition: value,
                  beforeValue: ' - '
                })
              } else if (value === dateRuleConditions.NULL) {
                setBeforeValue('')

                updateConditions({
                  beforeCondition: value as string,
                  beforeValue: 'NULL'
                })
              } else {
                setBeforeValue('')

                updateConditions({
                  beforeCondition: value as string,
                  beforeValue: ''
                })
              }
            }}
          />
          <InputDateTypeCondition
            option={beforeCondition}
            beforeValue={beforeValue}
            setBeforeValue={setBeforeValue}
            updateConditions={updateConditions}
          />
          <div className='mx-2'> Replace With </div>
          <InputDateTypeAction
            option={afterCondition}
            afterValue={afterValue}
            setAfterValue={setAfterValue}
            updateConditions={updateConditions}
          />
        </div>
      </div>
    )
  } else {
    return <label>This Attribute Is Currently Not Supported.</label>
  }
}

export default AttributeRule
